import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Download, FileSpreadsheet } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { MultiSelect } from "@/components/ui/MultiSelect";
import { previewParcelExport, startParcelExport } from "../api";
import { useParcelExportFacets, useTaskPolling } from "../hooks";
import { fmtArea, apiErr } from "../utils";
import type { ParcelExportFilters, ParcelExportPreview, ParcelExportResult } from "../types";
import { triggerBrowserDownload } from "@/utils/download";

// Scalar project filters — mirror the portfolio's single-choice dropdowns.
const DEADLINE_OPTS = [
  { value: "", label: "Termin: wszystkie" },
  { value: "overdue", label: "Po terminie" },
  { value: "soon", label: "≤ 14 dni" },
  { value: "approaching", label: "≤ 30 dni" },
  { value: "ok", label: "W terminie" },
  { value: "none", label: "Brak terminu" },
];
const PROGRESS_OPTS = [
  { value: "", label: "Postęp: wszystkie" },
  { value: "not_started", label: "Nierozpoczęte (0%)" },
  { value: "in_progress", label: "W toku" },
  { value: "done", label: "Zakończone (100%)" },
  { value: "problem", label: "Z problemem na działkach" },
];
const AGREEMENT_OPTS = [
  { value: "", label: "Uzgodnienia: wszystkie" },
  { value: "open", label: "Otwarte" },
  { value: "closed", label: "Zamknięte" },
];
const ROUTE_OPTS = [
  { value: "", label: "Trasa: wszystkie" },
  { value: "yes", label: "W aktualnej trasie" },
  { value: "no", label: "Poza aktualną trasą" },
];

const CHIPS_MAX_H = 132; // ~5 stacked chips, then the rest scroll (overflow)

// True once at least one filter is set — the default (nothing selected) yields 0 działki,
// so a deliberate selection is required before previewing/downloading.
function hasAnyFilter(f: ParcelExportFilters): boolean {
  const lists: (string[] | undefined)[] = [
    f.project_ids, f.coordinator_ids, f.branch_ids, f.client_ids,
    f.statuses, f.access_modes, f.voivodeships, f.counties, f.communes, f.land_uses,
  ];
  if (lists.some((l) => l && l.length > 0)) return true;
  return !!(f.search?.trim() || f.deadline_status || f.progress || f.agreements || f.in_current_route);
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="section-label">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

export function ParcelExportModal({ onClose }: { onClose: () => void }) {
  // Full (unfiltered) facets — a stable source of chip labels so a selected value never
  // loses its name even when cascading narrows it out of the live option list.
  const { data: fullFacets, isLoading: facetsLoading } = useParcelExportFacets({});
  const [f, setF] = useState<ParcelExportFilters>({});
  const [preview, setPreview] = useState<ParcelExportPreview | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const downloadedRef = useRef<string | null>(null);

  const upd = (patch: Partial<ParcelExportFilters>) => setF((s) => ({ ...s, ...patch }));
  const anyFilter = useMemo(() => hasAnyFilter(f), [f]);
  const filtersKey = JSON.stringify(f);

  // Any filter edit invalidates the shown result — force a fresh "Zastosuj filtry".
  useEffect(() => {
    setPreview(null);
    setTaskId(null);
    setError(null);
    downloadedRef.current = null;
  }, [filtersKey]);

  // Cascading: refetch narrowed option lists as filters change (debounced so rapid picks
  // don't spam the backend). placeholderData in the hook keeps the old options during refetch.
  const [debouncedFilters, setDebouncedFilters] = useState<ParcelExportFilters>({});
  useEffect(() => {
    const id = setTimeout(() => setDebouncedFilters(f), 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);
  const { data: liveFacets } = useParcelExportFacets(debouncedFilters);
  const facets = liveFacets ?? fullFacets;

  // id → name for every entity in the FULL list, so a chip keeps its label even when the
  // cascaded list no longer contains it.
  const entityLabels = useMemo(() => {
    const m = new Map<string, string>();
    if (fullFacets) {
      [...fullFacets.projects, ...fullFacets.coordinators, ...fullFacets.branches, ...fullFacets.clients]
        .forEach((e) => m.set(e.id, e.name));
    }
    return m;
  }, [fullFacets]);

  // Options = cascaded list ∪ any currently-selected value missing from it (kept as a chip).
  const entityOptions = (list: { id: string; name: string }[] | undefined, selected: string[]) => {
    const opts = (list ?? []).map((e) => ({ value: e.id, label: e.name }));
    const present = new Set(opts.map((o) => o.value));
    selected.forEach((id) => {
      if (!present.has(id)) opts.push({ value: id, label: entityLabels.get(id) ?? id });
    });
    return opts;
  };
  const stringOptions = (list: string[] | undefined, selected: string[]) => {
    const opts = (list ?? []).map((v) => ({ value: v, label: v }));
    const present = new Set(opts.map((o) => o.value));
    selected.forEach((v) => {
      if (!present.has(v)) opts.push({ value: v, label: v });
    });
    return opts;
  };

  const previewMut = useMutation({
    mutationFn: () => previewParcelExport(f),
    onSuccess: (data) => setPreview(data),
    onError: (e) => setError(apiErr(e)),
  });

  const startMut = useMutation({
    mutationFn: () => startParcelExport(f),
    onSuccess: (r) => setTaskId(r.task_id),
    onError: (e) => setError(apiErr(e)),
  });

  const task = useTaskPolling(taskId);
  const taskStatus = task.data?.status;
  const exporting = !!taskId && !["success", "failure", "cancelled"].includes(taskStatus ?? "");
  const result =
    taskStatus === "success" ? (task.data?.result as ParcelExportResult | null) : null;

  // Auto-download the CSV once it is ready (each file url only once).
  useEffect(() => {
    if (result?.file_url && downloadedRef.current !== result.file_url) {
      downloadedRef.current = result.file_url;
      triggerBrowserDownload(result.file_url, result.filename);
    }
  }, [result]);

  const applyPreview = () => {
    setError(null);
    previewMut.mutate();
  };

  const clearAll = () => setF({});

  const canDownload = anyFilter && !!preview && preview.count > 0 && !exporting;

  return (
    <Modal open onClose={onClose} title="Pobierz listę działek" size="lg">
      <div className="space-y-4 text-sm">
        <p className="text-xs text-[var(--color-text-muted)]">
          Wybierz filtry, aby zawęzić działki ze wszystkich projektów ZE. Domyślnie nic nie jest
          wybrane — ustaw przynajmniej jeden filtr, zastosuj go, a następnie pobierz plik CSV.
        </p>

        {facetsLoading ? (
          <div className="py-8 flex justify-center"><Spinner /></div>
        ) : (
          <>
            {/* ── Filtry projektu ── */}
            <div className="space-y-3">
              <h4 className="section-label text-[var(--color-text-primary)]">Filtry projektu</h4>
              <input
                className="input-base w-full"
                placeholder="Szukaj: nazwa przedsięwzięcia, klient, nr umowy…"
                value={f.search ?? ""}
                onChange={(e) => upd({ search: e.target.value })}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Przedsięwzięcie">
                  <MultiSelect
                    options={entityOptions(facets?.projects, f.project_ids ?? [])}
                    value={f.project_ids ?? []}
                    onChange={(v) => upd({ project_ids: v })}
                    placeholder="— wszystkie —"
                    chipsMaxHeight={CHIPS_MAX_H}
                  />
                </Field>
                <Field label="Prowadzący">
                  <MultiSelect
                    options={entityOptions(facets?.coordinators, f.coordinator_ids ?? [])}
                    value={f.coordinator_ids ?? []}
                    onChange={(v) => upd({ coordinator_ids: v })}
                    placeholder="— wszyscy —"
                    chipsMaxHeight={CHIPS_MAX_H}
                  />
                </Field>
                <Field label="Oddział">
                  <MultiSelect
                    options={entityOptions(facets?.branches, f.branch_ids ?? [])}
                    value={f.branch_ids ?? []}
                    onChange={(v) => upd({ branch_ids: v })}
                    placeholder="— wszystkie —"
                    chipsMaxHeight={CHIPS_MAX_H}
                  />
                </Field>
                <Field label="Klient">
                  <MultiSelect
                    options={entityOptions(facets?.clients, f.client_ids ?? [])}
                    value={f.client_ids ?? []}
                    onChange={(v) => upd({ client_ids: v })}
                    placeholder="— wszyscy —"
                    chipsMaxHeight={CHIPS_MAX_H}
                  />
                </Field>
                <Field label="Termin">
                  <select
                    className="input-base w-full"
                    value={f.deadline_status ?? ""}
                    onChange={(e) => upd({ deadline_status: e.target.value })}
                  >
                    {DEADLINE_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </Field>
                <Field label="Postęp">
                  <select
                    className="input-base w-full"
                    value={f.progress ?? ""}
                    onChange={(e) => upd({ progress: e.target.value })}
                  >
                    {PROGRESS_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </Field>
                <Field label="Uzgodnienia">
                  <select
                    className="input-base w-full"
                    value={f.agreements ?? ""}
                    onChange={(e) => upd({ agreements: e.target.value })}
                  >
                    {AGREEMENT_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </Field>
              </div>
            </div>

            {/* ── Filtry działki ── */}
            <div className="space-y-3 border-t border-[var(--color-border-muted)] pt-3">
              <h4 className="section-label text-[var(--color-text-primary)]">Filtry działki</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Status">
                  <MultiSelect
                    options={(facets?.statuses ?? []).map((s) => ({ value: s.value, label: s.label }))}
                    value={f.statuses ?? []}
                    onChange={(v) => upd({ statuses: v })}
                    placeholder="— wszystkie —"
                    chipsMaxHeight={CHIPS_MAX_H}
                  />
                </Field>
                <Field label="Tryb pozyskania dostępu">
                  <MultiSelect
                    options={(facets?.access_modes ?? []).map((s) => ({ value: s.value, label: s.label }))}
                    value={f.access_modes ?? []}
                    onChange={(v) => upd({ access_modes: v })}
                    placeholder="— wszystkie —"
                    chipsMaxHeight={CHIPS_MAX_H}
                  />
                </Field>
                <Field label="Województwo">
                  <MultiSelect
                    options={stringOptions(facets?.voivodeships, f.voivodeships ?? [])}
                    value={f.voivodeships ?? []}
                    onChange={(v) => upd({ voivodeships: v })}
                    placeholder="— wszystkie —"
                    chipsMaxHeight={CHIPS_MAX_H}
                  />
                </Field>
                <Field label="Powiat">
                  <MultiSelect
                    options={stringOptions(facets?.counties, f.counties ?? [])}
                    value={f.counties ?? []}
                    onChange={(v) => upd({ counties: v })}
                    placeholder="— wszystkie —"
                    chipsMaxHeight={CHIPS_MAX_H}
                  />
                </Field>
                <Field label="Gmina">
                  <MultiSelect
                    options={stringOptions(facets?.communes, f.communes ?? [])}
                    value={f.communes ?? []}
                    onChange={(v) => upd({ communes: v })}
                    placeholder="— wszystkie —"
                    chipsMaxHeight={CHIPS_MAX_H}
                  />
                </Field>
                <Field label="Rodzaj użytku">
                  <MultiSelect
                    options={stringOptions(facets?.land_uses, f.land_uses ?? [])}
                    value={f.land_uses ?? []}
                    onChange={(v) => upd({ land_uses: v })}
                    placeholder="— wszystkie —"
                    chipsMaxHeight={CHIPS_MAX_H}
                  />
                </Field>
                <Field label="Trasa">
                  <select
                    className="input-base w-full"
                    value={f.in_current_route ?? ""}
                    onChange={(e) => upd({ in_current_route: e.target.value })}
                  >
                    {ROUTE_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </Field>
              </div>
            </div>

            {/* ── akcje filtrów ── */}
            <div className="flex flex-wrap items-center gap-2 border-t border-[var(--color-border-muted)] pt-3">
              <Button size="sm" disabled={!anyFilter || previewMut.isPending} loading={previewMut.isPending} onClick={applyPreview}>
                Zastosuj filtry
              </Button>
              <Button size="sm" variant="ghost" onClick={clearAll} disabled={!anyFilter}>
                Wyczyść
              </Button>
              {!anyFilter && (
                <span className="text-[11px] font-mono text-[var(--color-text-muted)]">
                  Wybierz co najmniej jeden filtr.
                </span>
              )}
            </div>

            {error && <p className="text-xs text-[var(--color-primary)] font-mono">{error}</p>}

            {/* ── wynik: liczba + lista znalezionych działek ── */}
            {preview && (
              <div className="space-y-2 border-t border-[var(--color-border-muted)] pt-3">
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-[var(--color-text-primary)] font-mono">
                    {preview.count}
                  </span>
                  <span className="section-label">
                    {preview.count === 1 ? "działka" : "działek"} spełnia filtry
                  </span>
                </div>
                {preview.count === 0 ? (
                  <p className="text-xs text-[var(--color-text-muted)]">
                    Brak działek dla wybranych filtrów — zmień kryteria.
                  </p>
                ) : (
                  <div className="border border-[var(--color-border-muted)] max-h-64 overflow-y-auto">
                    <table className="w-full text-[11px] font-mono">
                      <thead className="sticky top-0 bg-[var(--color-bg-elevated)]">
                        <tr className="text-left text-[var(--color-text-muted)]">
                          <th className="px-2 py-1.5 font-normal uppercase tracking-wide">Nr działki</th>
                          <th className="px-2 py-1.5 font-normal uppercase tracking-wide">Przedsięwzięcie</th>
                          <th className="px-2 py-1.5 font-normal uppercase tracking-wide">Powiat</th>
                          <th className="px-2 py-1.5 font-normal uppercase tracking-wide">Gmina</th>
                          <th className="px-2 py-1.5 font-normal uppercase tracking-wide text-right">Pow.</th>
                          <th className="px-2 py-1.5 font-normal uppercase tracking-wide">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.results.map((r) => (
                          <tr key={r.id} className="border-t border-[var(--color-border-muted)]">
                            <td className="px-2 py-1 text-[var(--color-text-primary)]">{r.short_number || r.teryt}</td>
                            <td className="px-2 py-1 text-[var(--color-text-secondary)] max-w-[180px] truncate">{r.project_name}</td>
                            <td className="px-2 py-1 text-[var(--color-text-secondary)]">{r.county || "—"}</td>
                            <td className="px-2 py-1 text-[var(--color-text-secondary)]">{r.commune || "—"}</td>
                            <td className="px-2 py-1 text-[var(--color-text-secondary)] text-right">{fmtArea(r.area_m2)}</td>
                            <td className="px-2 py-1 text-[var(--color-text-secondary)]">{r.status_display}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {preview.truncated && (
                      <div className="px-2 py-1.5 text-[10px] text-[var(--color-text-muted)] border-t border-[var(--color-border-muted)]">
                        Podgląd pierwszych {preview.limit} z {preview.count} — plik CSV zawiera wszystkie.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── stan zadania eksportu ── */}
            {taskId && (
              <div className="p-3 border border-[var(--color-border-muted)] bg-[var(--color-bg-elevated)] space-y-2 text-xs">
                <div className="flex items-center gap-2">
                  {exporting && <Spinner className="h-3 w-3" />}
                  <span className="font-medium text-[var(--color-text-secondary)]">
                    {taskStatus === "success"
                      ? "Plik gotowy — pobieranie rozpoczęte."
                      : taskStatus === "failure"
                        ? "Eksport nieudany."
                        : "Tworzenie pliku CSV…"}
                  </span>
                </div>
                {result?.file_url && (
                  <a
                    href={result.file_url}
                    download={result.filename}
                    className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline font-mono"
                  >
                    <Download size={12} /> Pobierz ponownie ({result.count} działek)
                  </a>
                )}
                {task.data?.log && taskStatus !== "success" && (
                  <pre className="text-[10px] font-mono text-[var(--color-text-muted)] whitespace-pre-wrap max-h-32 overflow-y-auto bg-[var(--color-bg-surface)] p-2 border border-[var(--color-border-muted)]">
                    {task.data.log}
                  </pre>
                )}
              </div>
            )}

            {/* ── stopka ── */}
            <div className="flex items-center justify-end gap-2 border-t border-[var(--color-border-muted)] pt-3">
              {exporting && (
                <span className="text-[11px] font-mono text-[var(--color-text-muted)] mr-auto">
                  Możesz zamknąć — plik tworzy się w tle.
                </span>
              )}
              <Button variant="ghost" size="sm" onClick={onClose}>Zamknij</Button>
              <Button
                size="sm"
                disabled={!canDownload}
                loading={startMut.isPending || exporting}
                onClick={() => startMut.mutate()}
              >
                <FileSpreadsheet size={14} /> Pobierz CSV
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
